import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  APPLICATION_STAGE_MAP,
  MAX_PIPELINE_STAGES,
  STAGE_KEY_PATTERN,
  APPLICATION_STATUS_MAP,
  CANDIDATE_SOURCES,
  CANDIDATE_SOURCE_MAP,
  EVALUATION_CRITERION_DEFS,
  INTERVIEW_KIND_DEFS,
  INTERVIEW_KIND_MAP,
  RECOMMENDATION_DEFS,
  SCORE_LABELS,
  SCORE_MAX,
  averageScore,
  nextStageOf,
  suggestStageKey,
  type ApplicationStage,
  type ApplicationStatus,
  type InterviewKind,
  type InterviewStatus,
  type Recommendation,
} from "@goyone/shared";
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  SearchBox,
} from "../../components/ui";
import { CustomFieldInputs } from "../../components/CustomFields";
import { Icon } from "../../components/Icon";
import { ReachIcons } from "../../components/Reach";
import { OfferSection } from "./Offer";
import { Fact, FactGrid } from "../../components/staff";
import { useToast } from "../../components/Toast";
import { api, qs } from "../../lib/api";
import {
  dateTime,
  initials,
  num,
  plural,
  relative,
  WORDS,
} from "../../lib/format";
import { useAuth } from "../../auth/AuthProvider";

interface Verdict {
  count: number;
  average: number | null;
  lean: number;
  split: boolean;
  label: string | null;
}

interface Card {
  id: string;
  stage: string;
  stageLabel: string;
  stageKind: string;
  stageAction: string | null;
  status: ApplicationStatus;
  statusLabel: string;
  statusKind: string;
  appliedAt: string;
  stageChangedAt: string;
  candidateId: string;
  candidateCode: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  headline: string | null;
  source: string;
  sourceLabel: string;
  cvAssetId: string | null;
  job: { id: string; title: string; code: string | null };
  /* تُملأ على بطاقات اللوحة وحدها. */
  verdict?: Verdict;
  nextInterview?: { at: string; kindLabel: string } | null;
}

interface Column {
  key: string;
  label: string;
  kind: string;
  description: string | null;
  action: string | null;
  /** عمودٌ أُوقف وفيه طلبات — يُعرض ليُنقل من فيه، لا ليُستعمل. */
  stopped: boolean;
  cards: Card[];
}

/** عمودُ المسار كما يردّه مسارُ الضبط. */
interface StageRow {
  id: string;
  key: string;
  label: string;
  kind: string;
  action: string | null;
  description: string | null;
  order: number;
  isCore: boolean;
  isActive: boolean;
  required: boolean;
  count: number;
}

interface Board {
  columns: Column[];
  total: number;
}

interface EventRow {
  id: string;
  kind: string;
  fromStage: string | null;
  fromLabel: string | null;
  toStage: string | null;
  toLabel: string | null;
  note: string | null;
  at: string;
}

interface AnswerRow {
  key: string;
  label: string;
  type: string;
  value: unknown;
  orphan?: boolean;
}

interface InterviewRow {
  id: string;
  kind: string;
  kindLabel: string;
  kindTone: string;
  stage: string;
  stageLabel: string;
  scheduledAt: string;
  minutes: number;
  place: string | null;
  meetingUrl: string | null;
  interviewerId: string | null;
  status: InterviewStatus;
  statusLabel: string;
  statusKind: string;
  pending: boolean;
  happened: boolean;
  outcome: string | null;
  applicationId: string;
  candidateId: string;
  name: string;
  phone: string | null;
  job: { id: string; title: string };
}

interface EvaluationRow {
  id: string;
  interviewId: string | null;
  reviewerId: string;
  stage: string;
  stageLabel: string;
  scores: Record<string, number>;
  recommendation: string;
  recommendationLabel: string;
  recommendationKind: string;
  notes: string | null;
  at: string;
}

/** ما كُتب في ملفّ الوظيفة ويُملأ منه عرضُ العمل. */
export interface OfferBasis {
  deptKey: string | null;
  employmentType: string;
  salaryMin: number | null;
  salaryMax: number | null;
}

interface ApplicationView extends Card {
  offerBasis: OfferBasis;
  notes: string | null;
  rejectReason: string | null;
  answers: AnswerRow[];
  events: EventRow[];
  verdict: Verdict;
  interviews: InterviewRow[];
  evaluations: EvaluationRow[];
}

interface JobLite {
  id: string;
  title: string;
  status: string;
  form: { id: string; name: string } | null;
}

/* ==========================================================================
   لوحة المسار
   ========================================================================== */

/**
 * أعمدة المراحل.
 *
 * الأعمدةُ من الكتالوج لا من البيانات: عمودٌ فارغٌ يجب أن يظهر — «لا أحد
 * في المقابلة النهائية» معلومةٌ، وإخفاءُ العمود يجعلها غيابًا لا يُلاحَظ.
 */
export function RecruitPipeline() {
  const [params, setParams] = useSearchParams();
  const { atLeast } = useAuth();
  const [adding, setAdding] = useState(false);
  const [editingStages, setEditingStages] = useState(false);
  /* `open` في الرابط: أجندةُ المواعيد تفتح ملفَّ الطلب مباشرةً. */
  const [open, setOpen] = useState<string | null>(params.get("open"));
  const [search, setSearch] = useState("");

  const jobPostId = params.get("jobPostId") ?? "";
  const focus = params.get("stage") ?? "";

  const board = useQuery({
    queryKey: ["recruit", "board", jobPostId],
    queryFn: () =>
      api<Board>(
        `/hr/recruit/board${qs({ jobPostId: jobPostId || undefined })}`,
      ),
  });

  const jobs = useQuery({
    queryKey: ["recruit", "jobs", "lite"],
    queryFn: () => api<JobLite[]>("/hr/recruit/jobs"),
  });

  const term = search.trim().toLowerCase();
  const columns = (board.data?.columns ?? []).map((c) => ({
    ...c,
    cards: term
      ? c.cards.filter(
          (x) =>
            x.name.toLowerCase().includes(term) ||
            (x.phone ?? "").includes(term) ||
            (x.candidateCode ?? "").toLowerCase().includes(term),
        )
      : c.cards,
  }));

  const shown = columns.reduce((s, c) => s + c.cards.length, 0);

  return (
    <div className="page">
      <PageHead
        title="مسار التوظيف"
        subtitle={
          board.data ? `${plural(shown, WORDS.application)} قائمة` : undefined
        }
        actions={
          atLeast("MANAGER") && (
            <>
              <button className="btn" onClick={() => setEditingStages(true)}>
                <Icon name="layers" size={15} />
                أعمدة المسار
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setAdding(true)}
              >
                <Icon name="plus" size={15} />
                أضف مرشّحًا
              </button>
            </>
          )
        }
      />

      <div className="toolbar">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="بحث بالاسم أو الهاتف أو الكود"
        />

        <select
          className="select"
          value={jobPostId}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set("jobPostId", e.target.value);
            else next.delete("jobPostId");
            setParams(next, { replace: true });
          }}
        >
          <option value="">كل الوظائف</option>
          {(jobs.data ?? []).map((j) => (
            <option key={j.id} value={j.id}>
              {j.title}
            </option>
          ))}
        </select>
      </div>

      {board.isError && (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      )}

      {board.isPending && <div className="skeleton" style={{ height: 320 }} />}

      {board.data && board.data.total === 0 && (
        <EmptyState
          title="المسار خالٍ"
          text="لا طلبات قائمة. انشر رابط وظيفةٍ ليصلك التقديم، أو أدخل من وصلك بالبريد."
          action={
            atLeast("MANAGER") ? (
              <button
                className="btn btn-primary"
                onClick={() => setAdding(true)}
              >
                أضف مرشّحًا
              </button>
            ) : undefined
          }
        />
      )}

      {board.data && board.data.total > 0 && (
        <div className="rc-board">
          {columns.map((c) => (
            <section
              key={c.key}
              className={`rc-col${focus === c.key ? " is-focus" : ""}`}
            >
              <header className="rc-col-head">
                <Badge kind={c.kind}>{c.label}</Badge>
                {/*
                  عمودٌ أُوقف وفيه طلبات: يُعرض ليُفرَّغ لا ليُستعمل. وإخفاؤه
                  كان يجعل من فيه يختفي من النظام كلِّه وهو ما زال ينتظر.
                */}
                {c.stopped && <Badge kind="warning">موقوف</Badge>}
                <span className="num tiny mute">{num(c.cards.length)}</span>
              </header>

              {c.cards.length === 0 ? (
                <p className="rc-col-empty tiny mute">
                  {c.description ?? "لا وصفَ لهذا العمود."}
                </p>
              ) : (
                <div className="rc-col-body">
                  {c.cards.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      className="rc-card"
                      onClick={() => setOpen(card.id)}
                    >
                      <span className="rc-card-top">
                        <span className="rc-avatar" aria-hidden="true">
                          {initials(card.name)}
                        </span>
                        <span className="rc-card-id">
                          <strong>{card.name}</strong>
                          <span className="tiny mute">
                            {card.headline ?? card.candidateCode ?? "—"}
                          </span>
                        </span>
                      </span>

                      {!jobPostId && (
                        <span className="rc-chip">{card.job.title}</span>
                      )}

                      {/*
                        الاختلافُ يُرى على البطاقة لا خلف نقرة: مرشّحٌ
                        اختلف عليه المقيّمون هو أوّلُ من يستحقّ الوقوف.
                      */}
                      {card.verdict && card.verdict.count > 0 && (
                        <span className="row" style={{ gap: 6 }}>
                          <Badge
                            kind={
                              card.verdict.split
                                ? "warning"
                                : card.verdict.lean > 0
                                  ? "success"
                                  : card.verdict.lean < 0
                                    ? "danger"
                                    : "neutral"
                            }
                          >
                            {card.verdict.label}
                          </Badge>
                        </span>
                      )}

                      {card.nextInterview && (
                        <span className="tiny rc-due">
                          <Icon name="calendar" size={12} />
                          {card.nextInterview.kindLabel}{" "}
                          {dateTime(card.nextInterview.at)}
                        </span>
                      )}

                      <span className="tiny mute">
                        هنا منذ {relative(card.stageChangedAt)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {adding && <AddCandidateDialog onClose={() => setAdding(false)} />}

      {editingStages && (
        <StagesDialog onClose={() => setEditingStages(false)} />
      )}
      {open && <ApplicationDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/* ==========================================================================
   ملفّ الطلب
   ========================================================================== */

export function ApplicationDetail() {
  const { id = "" } = useParams();
  return (
    <div className="page">
      <PageHead title="طلب التوظيف" />
      <ApplicationBody id={id} />
    </div>
  );
}

function ApplicationDialog({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: ["recruit", "application", id],
    queryFn: () => api<ApplicationView>(`/hr/recruit/applications/${id}`),
  });

  return (
    <Modal title={detail.data?.name ?? "طلب التوظيف"} onClose={onClose} wide>
      <ApplicationBody id={id} onDone={onClose} />
    </Modal>
  );
}

function ApplicationBody({ id, onDone }: { id: string; onDone?: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { atLeast } = useAuth();
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState<ApplicationStatus | null>(null);
  const [booking, setBooking] = useState(false);
  const [evaluating, setEvaluating] = useState<{
    interviewId: string | null;
  } | null>(null);

  const detail = useQuery({
    queryKey: ["recruit", "application", id],
    queryFn: () => api<ApplicationView>(`/hr/recruit/applications/${id}`),
  });

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ["recruit"] });

  const stageList = useQuery({
    queryKey: ["recruit", "stages"],
    queryFn: () => api<StageRow[]>("/hr/recruit/stages"),
  });

  const move = useMutation({
    /* المفتاحُ نصٌّ: الأعمدةُ صارت بياناتِ الشركة لا قائمةً في الشيفرة. */
    mutationFn: (stage: string) =>
      api(`/hr/recruit/applications/${id}/move`, {
        method: "POST",
        body: { stage },
      }),
    onSuccess: () => {
      refresh();
      toast("تحرّك الطلب");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التحريك"),
  });

  /**
   * إقفالُ الموعد.
   *
   * «لم يحضر» و«أُلغي» حالتان لا واحدة: الأولى تخصّ المرشّح وتُقرأ في
   * قراره، والثانية تخصّنا. وخلطُهما يجعل تخلُّفَ المرشّحين غيرَ مرئيّ.
   */
  const closeInterview = useMutation({
    mutationFn: ({
      id: ivId,
      status,
    }: {
      id: string;
      status: InterviewStatus;
    }) =>
      api(`/hr/recruit/interviews/${ivId}`, {
        method: "PATCH",
        body: { status },
      }),
    onSuccess: () => {
      refresh();
      toast("سُجّل الموعد");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التسجيل"),
  });

  const addNote = useMutation({
    mutationFn: () =>
      api(`/hr/recruit/applications/${id}/notes`, {
        method: "POST",
        body: { note: note.trim() },
      }),
    onSuccess: () => {
      refresh();
      setNote("");
      toast("أُضيفت الملاحظة");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّرت الإضافة"),
  });

  if (detail.isError) {
    return (
      <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
    );
  }
  if (detail.isPending) {
    return <div className="skeleton" style={{ height: 240 }} />;
  }

  const a = detail.data;
  const stage = a.stage;
  /*
   * الأعمدةُ تُقرأ من الخادم لا من الكتالوج.
   *
   * المسارُ صار بياناتِ الشركة، وقائمةٌ ثابتةٌ هنا كانت تخفي كلَّ عمودٍ
   * تضيفه وتعرض كلَّ عمودٍ أوقفته.
   */
  const stages = stageList.data ?? [];
  const live = stages.filter((x) => x.isActive);
  const forward = nextStageOf(live, stage);
  const canAct = atLeast("MANAGER") && a.status === "ACTIVE";
  const forwardLabel = live.find((x) => x.key === forward)?.label ?? forward;
  const atEnd = forward === stage;

  return (
    <>
      <div className="rc-head">
        <span className="rc-avatar rc-avatar-lg" aria-hidden="true">
          {initials(a.name)}
        </span>
        <div>
          <strong className="rc-head-name">{a.name}</strong>
          <div className="tiny mute">
            {a.headline ?? "—"} · {a.candidateCode ?? "—"}
          </div>
          <div className="row mt-2" style={{ gap: "var(--sp-2)" }}>
            <Badge kind={a.stageKind}>{a.stageLabel}</Badge>
            <Badge kind={a.statusKind}>{a.statusLabel}</Badge>
            <Link className="link tiny" to={`/hr/recruit/jobs/${a.job.id}`}>
              {a.job.title}
            </Link>
          </div>
        </div>
      </div>

      {/*
        الوصولُ إليه بأدوات النظام لا من جهاز الموظّف.

        المكالمةُ تُسجَّل في مركز الاتصال منسوبةً إليه، والرسالةُ تُحفظ في
        محادثته — والردُّ من الهاتف يضيع في جهاز من ردّ. والمكالمةُ معفاةٌ
        من سياسة تسجيل الورديات: تلك سياسةُ مركز الاتصال أُعلنت لموظّفيه.
      */}
      <div className="rc-contact mt-4">
        <ReachIcons
          name={a.name}
          phone={a.phone}
          email={a.email}
          candidateId={a.candidateId}
          skipRecording
          size={16}
        />
        <span className="tiny mute" dir="ltr">
          {a.phone ?? "—"}
        </span>
        <span className="rc-chip">{a.sourceLabel}</span>
      </div>

      {a.status === "REJECTED" && a.rejectReason && (
        <Notice kind="danger" title="سبب الاستبعاد" icon="x">
          {a.rejectReason}
        </Notice>
      )}

      {a.status !== "ACTIVE" && a.status !== "REJECTED" && (
        <Notice
          kind={a.status === "HIRED" ? "success" : "info"}
          title={a.statusLabel}
          icon="info"
        >
          {a.status === "WAITLIST"
            ? "مؤهَّلٌ وقف عند هذه المرحلة ولا شاغر — ملفُّه محفوظٌ لفرصةٍ قادمة."
            : "خرج من مسار هذه الوظيفة."}
        </Notice>
      )}

      {canAct && (
        <div className="rc-actions mt-4">
          {!atEnd && (
            <button
              className="btn btn-primary"
              onClick={() => move.mutate(forward)}
              disabled={move.isPending}
            >
              <Icon name="check" size={14} />
              انقله إلى {forwardLabel}
            </button>
          )}

          <select
            className="select"
            value={live.some((x) => x.key === stage) ? stage : ""}
            onChange={(e) => e.target.value && move.mutate(e.target.value)}
          >
            {/*
              من وقف في عمودٍ أُوقف لا مكانَ له في القائمة: يُعرض سطرٌ
              يقول أين هو، ويُختار منه عمودٌ قائم.
            */}
            {!live.some((x) => x.key === stage) && (
              <option value="">{a.stageLabel} — عمودٌ موقوف</option>
            )}
            {live.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </select>

          <button className="btn" onClick={() => setDeciding("WAITLIST")}>
            قائمة الانتظار
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setDeciding("REJECTED")}
          >
            <Icon name="x" size={14} />
            استبعاد
          </button>
        </div>
      )}

      {!canAct && a.status !== "ACTIVE" && atLeast("MANAGER") && (
        <div className="rc-actions mt-4">
          <button className="btn" onClick={() => setDeciding("ACTIVE")}>
            <Icon name="refresh" size={14} />
            أعِده إلى المسار
          </button>
        </div>
      )}

      <div className="mt-4">
        <div className="row" style={{ gap: "var(--sp-2)" }}>
          <h3 className="card-title" style={{ flex: 1 }}>
            المقابلات والتقييم
          </h3>
          {atLeast("MANAGER") && a.status === "ACTIVE" && (
            <>
              <button className="btn btn-sm" onClick={() => setBooking(true)}>
                <Icon name="calendar" size={14} />
                احجز موعدًا
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setEvaluating({ interviewId: null })}
              >
                <Icon name="star" size={14} />
                اكتب تقييمًا
              </button>
            </>
          )}
        </div>

        {/*
          الخلاصةُ تُعلن الاختلافَ ولا تذيبه في متوسّط: مرشّحٌ رشّحه واحدٌ
          ورفضه آخرُ ليس «متوسّطًا» — هو حالةٌ تحتاج حديثًا بين الاثنين.
        */}
        {a.verdict.count > 0 && (
          <div className="rc-verdict">
            <Badge
              kind={
                a.verdict.split
                  ? "warning"
                  : a.verdict.lean > 0
                    ? "success"
                    : a.verdict.lean < 0
                      ? "danger"
                      : "neutral"
              }
            >
              {a.verdict.label}
            </Badge>
            <span className="tiny mute">
              {plural(a.verdict.count, WORDS.record)}
              {a.verdict.average !== null
                ? ` · متوسّط ${a.verdict.average} من ${SCORE_MAX}`
                : " · بلا درجات"}
            </span>
          </div>
        )}

        {a.interviews.length === 0 && a.evaluations.length === 0 && (
          <p className="tiny mute">
            لا مواعيد ولا تقييمات بعد. والتقدّم من «مقابلة» أو «مقابلة نهائية»
            يحتاج تقييمًا مكتوبًا.
          </p>
        )}

        {a.interviews.length > 0 && (
          <ol className="rc-log mt-2">
            {a.interviews.map((iv) => (
              <li key={iv.id}>
                <span className="rc-log-when tiny mute">
                  {dateTime(iv.scheduledAt)}
                </span>
                <span className="rc-log-what">
                  <span className="row" style={{ gap: "var(--sp-2)" }}>
                    <Badge kind={iv.kindTone}>{iv.kindLabel}</Badge>
                    <Badge kind={iv.statusKind}>{iv.statusLabel}</Badge>
                    <span className="tiny mute">{iv.minutes} دقيقة</span>
                  </span>
                  {iv.meetingUrl && (
                    <a
                      className="link tiny"
                      href={iv.meetingUrl}
                      target="_blank"
                      rel="noreferrer"
                      dir="ltr"
                    >
                      {iv.meetingUrl}
                    </a>
                  )}
                  {iv.place && <div className="tiny mute">{iv.place}</div>}
                  {iv.outcome && <div className="tiny">{iv.outcome}</div>}
                  {atLeast("MANAGER") && (
                    <span className="row mt-2" style={{ gap: "var(--sp-2)" }}>
                      {iv.pending && (
                        <>
                          <button
                            className="btn btn-sm"
                            onClick={() =>
                              closeInterview.mutate({
                                id: iv.id,
                                status: "DONE",
                              })
                            }
                          >
                            تمّ
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() =>
                              closeInterview.mutate({
                                id: iv.id,
                                status: "NO_SHOW",
                              })
                            }
                          >
                            لم يحضر
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() =>
                              closeInterview.mutate({
                                id: iv.id,
                                status: "CANCELLED",
                              })
                            }
                          >
                            ألغِ
                          </button>
                        </>
                      )}
                      {iv.happened && (
                        <button
                          className="btn btn-sm"
                          onClick={() => setEvaluating({ interviewId: iv.id })}
                        >
                          قيّم هذا اللقاء
                        </button>
                      )}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}

        {a.evaluations.length > 0 && (
          <div className="rc-evals mt-2">
            {a.evaluations.map((ev) => (
              <div className="rc-eval" key={ev.id}>
                <div className="row" style={{ gap: "var(--sp-2)" }}>
                  <Badge kind={ev.recommendationKind}>
                    {ev.recommendationLabel}
                  </Badge>
                  <span className="tiny mute">{ev.stageLabel}</span>
                  <span className="tiny mute">{dateTime(ev.at)}</span>
                </div>
                {Object.keys(ev.scores).length > 0 && (
                  <div className="rc-scores">
                    {EVALUATION_CRITERION_DEFS.filter(
                      (c) => ev.scores[c.key] !== undefined,
                    ).map((c) => (
                      <span key={c.key} className="rc-score">
                        {c.label}
                        <b className="num">{ev.scores[c.key]}</b>
                      </span>
                    ))}
                  </div>
                )}
                {ev.notes && <p className="tiny">{ev.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <OfferSection
        applicationId={id}
        candidateName={a.name}
        jobTitle={a.job.title}
        basis={a.offerBasis}
        canAct={canAct}
      />

      {a.answers.length > 0 && (
        <div className="mt-4">
          <h3 className="card-title">إجابات الاستمارة</h3>
          <FactGrid>
            {a.answers.map((x) => (
              <Fact
                key={x.key}
                icon={x.orphan ? "alert" : "note"}
                label={x.orphan ? `${x.label} — حقلٌ حُذف` : x.label}
                value={answerText(x.value)}
                wide={x.type === "MULTILINE"}
              />
            ))}
          </FactGrid>
        </div>
      )}

      <div className="mt-4">
        <h3 className="card-title">السجلّ</h3>

        {atLeast("MANAGER") && (
          <div className="row mt-2" style={{ gap: "var(--sp-2)" }}>
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ما دار في المكالمة، أو لماذا تأخّر."
            />
            <button
              className="btn"
              onClick={() => addNote.mutate()}
              disabled={!note.trim() || addNote.isPending}
            >
              أضف
            </button>
          </div>
        )}

        <ol className="rc-log mt-2">
          {a.events.map((e) => (
            <li key={e.id}>
              <span className="rc-log-when tiny mute">{dateTime(e.at)}</span>
              <span className="rc-log-what">
                {logLine(e)}
                {e.note && <div className="tiny mute">{e.note}</div>}
              </span>
            </li>
          ))}
        </ol>
      </div>

      {deciding && (
        <DecideDialog
          id={id}
          status={deciding}
          onClose={() => setDeciding(null)}
          onDone={() => {
            setDeciding(null);
            onDone?.();
          }}
        />
      )}

      {booking && <ScheduleDialog id={id} onClose={() => setBooking(false)} />}

      {evaluating && (
        <EvaluateDialog
          id={id}
          interviewId={evaluating.interviewId}
          existing={a.evaluations.find(
            (e) => e.interviewId === evaluating.interviewId,
          )}
          onClose={() => setEvaluating(null)}
        />
      )}
    </>
  );
}

/* ==========================================================================
   حجز موعد
   ========================================================================== */

/**
 * موعدُ لقاء.
 *
 * يُكتب صفّان: المقابلةُ في التوظيف، والموعدُ في تقويم «مقابلات التوظيف».
 * والثاني هو من يُذكَّر به ويُرى في تقويم الشركة — فبدونه موعدٌ لا يعلمه
 * أحد.
 */
function ScheduleDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [kind, setKind] = useState<InterviewKind>("PHONE");
  const [at, setAt] = useState(() => {
    const t = new Date(Date.now() + 24 * 60 * 60 * 1000);
    t.setMinutes(0, 0, 0);
    return new Date(t.getTime() - t.getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 16);
  });
  const [minutes, setMinutes] = useState(
    String(INTERVIEW_KIND_MAP.PHONE.minutes),
  );
  const [place, setPlace] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [note, setNote] = useState("");

  const def = INTERVIEW_KIND_MAP[kind];
  const valid =
    at !== "" &&
    (!def.needsLink || meetingUrl.trim() !== "") &&
    (!def.needsPlace || place.trim() !== "");

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/recruit/applications/${id}/interviews`, {
        method: "POST",
        body: {
          kind,
          scheduledAt: new Date(at).toISOString(),
          minutes: Number(minutes) || def.minutes,
          place: place.trim() || null,
          meetingUrl: meetingUrl.trim() || null,
          note: note.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
      toast("حُجز الموعد وأُضيف إلى التقويم");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحجز"),
  });

  return (
    <Modal
      title="احجز موعدًا"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحجز" : "احجز"}
          </button>
        </>
      }
    >
      <div className="rc-kinds">
        {INTERVIEW_KIND_DEFS.map((d) => (
          <button
            key={d.key}
            type="button"
            className={`rc-kind${kind === d.key ? " is-active" : ""}`}
            aria-pressed={kind === d.key}
            onClick={() => {
              setKind(d.key);
              setMinutes(String(d.minutes));
            }}
          >
            <strong>
              <Badge kind={d.kind}>{d.label}</Badge>
            </strong>
            <span className="tiny mute">{d.description}</span>
          </button>
        ))}
      </div>

      <div className="form-grid mt-4">
        <div className="field">
          <label className="label req" htmlFor="iv-at">
            الموعد
          </label>
          <input
            id="iv-at"
            className="input num"
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="iv-min">
            المدّة بالدقائق
          </label>
          <input
            id="iv-min"
            className="input num"
            type="number"
            min={5}
            max={480}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </div>

        {def.needsLink && (
          <div className="field span-2">
            <label className="label req" htmlFor="iv-url">
              الصق رابط الاجتماع
            </label>
            <input
              id="iv-url"
              className="input"
              dir="ltr"
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              placeholder="https://…"
            />
            <span className="tiny mute">
              النظام لا يُنشئ غرفةً في زووم أو مِيت — أنشئها هناك والصق رابطها
              هنا ليصل إلى المرشّح ويظهر في التذكير.
            </span>
          </div>
        )}

        {def.needsPlace && (
          <div className="field span-2">
            <label className="label req" htmlFor="iv-place">
              العنوان كما يُكتب للمرشّح
            </label>
            <input
              id="iv-place"
              className="input"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              placeholder="٢٧ شارع النصر، المعادي — الدور الثالث"
            />
          </div>
        )}

        <div className="field span-2">
          <label className="label" htmlFor="iv-note">
            ملاحظة على الموعد
          </label>
          <textarea
            id="iv-note"
            className="input"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   التقييم
   ========================================================================== */

/**
 * حكمٌ على مرشّح.
 *
 * التوصيةُ مطلوبةٌ والدرجاتُ اختيارية: من قابل مرشّحًا في مكالمةٍ قصيرة
 * لا يحكم على انسجامه مع الفريق، وإجبارُه على درجةٍ في كلّ معيارٍ يُنتج
 * أوساطًا مكتوبةً لتملأ الخانة لا لتقول شيئًا. والحسابُ لا يقلب متوسّطًا
 * إلى قبول — من يعرف أنّ رقمَه يقرّر يكتب الرقمَ الذي يُنتج ما يريد.
 */
function EvaluateDialog({
  id,
  interviewId,
  existing,
  onClose,
}: {
  id: string;
  interviewId: string | null;
  existing?: EvaluationRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [scores, setScores] = useState<Record<string, number>>(
    existing?.scores ?? {},
  );
  const [recommendation, setRecommendation] = useState<Recommendation>(
    (existing?.recommendation as Recommendation) ?? "MAYBE",
  );
  const [notes, setNotes] = useState(existing?.notes ?? "");

  const needsReason = recommendation === "NO";
  const valid = !needsReason || notes.trim() !== "";
  const avg = averageScore(scores);

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/recruit/applications/${id}/evaluations`, {
        method: "POST",
        body: {
          interviewId,
          scores,
          recommendation,
          notes: notes.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast(existing ? "حُدّث تقييمك" : "سُجّل تقييمك");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التسجيل"),
  });

  return (
    <Modal
      title={existing ? "عدّل تقييمك" : "اكتب تقييمًا"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ التسجيل" : "سجّل"}
          </button>
        </>
      }
    >
      <p className="tiny mute">
        التوصيةُ هي القرار، والدرجاتُ ترتّب المتقاربين. والنظامُ لا يقلب
        متوسّطًا إلى قبولٍ أو رفض.
      </p>

      <div className="rc-recos mt-2">
        {RECOMMENDATION_DEFS.map((d) => (
          <button
            key={d.key}
            type="button"
            className={`rc-reco${recommendation === d.key ? " is-active" : ""}`}
            aria-pressed={recommendation === d.key}
            onClick={() => setRecommendation(d.key)}
          >
            <strong>
              <Badge kind={d.kind}>{d.label}</Badge>
            </strong>
            <span className="tiny mute">{d.description}</span>
          </button>
        ))}
      </div>

      <h3 className="card-title mt-4">
        الدرجات{" "}
        <span className="tiny mute">
          اختيارية — {avg === null ? "بلا درجات" : `المتوسّط ${avg}`}
        </span>
      </h3>

      <div className="rc-criteria">
        {EVALUATION_CRITERION_DEFS.map((c) => (
          <div className="rc-criterion" key={c.key}>
            <div>
              <strong>{c.label}</strong>
              <div className="tiny mute">{c.hint}</div>
            </div>
            <div className="rc-scale">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`rc-dot${scores[c.key] === n ? " is-on" : ""}`}
                  aria-pressed={scores[c.key] === n}
                  title={SCORE_LABELS[n]}
                  onClick={() =>
                    setScores((p) => {
                      const next = { ...p };
                      /* النقرةُ على الدرجة نفسِها تمحوها — لا حبسَ في رقم. */
                      if (next[c.key] === n) delete next[c.key];
                      else next[c.key] = n;
                      return next;
                    })
                  }
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="field mt-4">
        <label
          className={`label${needsReason ? " req" : ""}`}
          htmlFor="ev-notes"
        >
          ما رأيتَه
        </label>
        <textarea
          id="ev-notes"
          className="input"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="ما قاله وما أظهره — لا انطباعًا عامًّا."
        />
        {needsReason && (
          <span className="tiny mute">
            عدم الترشيح يحتاج سببًا: رفضٌ بلا سببٍ لا يُراجَع ولا يُقاس.
          </span>
        )}
      </div>
    </Modal>
  );
}

function answerText(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (value === true) return "نعم";
  if (value === false) return "لا";
  return String(value);
}

function logLine(e: EventRow): string {
  if (e.kind === "APPLIED") return "تقدَّم";
  if (e.kind === "NOTE") return "ملاحظة";
  if (e.kind === "STAGE") {
    return e.fromLabel
      ? `انتقل من ${e.fromLabel} إلى ${e.toLabel}`
      : `انتقل إلى ${e.toLabel}`;
  }
  const to = e.toStage
    ? (APPLICATION_STATUS_MAP[e.toStage as ApplicationStatus]?.label ??
      e.toStage)
    : "";
  return `تغيّرت حالته إلى ${to}`;
}

/* ==========================================================================
   القرار
   ========================================================================== */

function DecideDialog({
  id,
  status,
  onClose,
  onDone,
}: {
  id: string;
  status: ApplicationStatus;
  onClose: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");

  const needsReason = status === "REJECTED";
  const valid = !needsReason || reason.trim().length > 0;

  const decide = useMutation({
    mutationFn: () =>
      api(`/hr/recruit/applications/${id}/decide`, {
        method: "POST",
        body: { status, reason: reason.trim() || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast("سُجّل القرار");
      onDone();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التسجيل"),
  });

  return (
    <Modal
      title={APPLICATION_STATUS_MAP[status].label}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => decide.mutate()}
            disabled={!valid || decide.isPending}
          >
            {decide.isPending ? "جارٍ التسجيل" : "سجّل"}
          </button>
        </>
      }
    >
      {needsReason ? (
        <>
          <p className="tiny mute">
            السبب يُكتب ولا يُترك: من فُرز بلا سبب لا يُعرف بعد شهرٍ لماذا فُرز،
            ولا يُقاس أيُّ الأسباب يتكرّر — وذاك ما يُصلح الإعلان نفسه.
          </p>
          <div className="field mt-2">
            <label className="label req" htmlFor="dec-reason">
              سبب الاستبعاد
            </label>
            <textarea
              id="dec-reason"
              className="input"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="الخبرة أقلّ من المطلوب في الإعلان."
            />
          </div>
        </>
      ) : (
        <>
          <p className="tiny mute">
            {status === "WAITLIST"
              ? "يخرج من مسار هذه الوظيفة ويبقى ملفُّه — تبقى مرحلتُه كما هي فيُعرف أين وقف."
              : status === "ACTIVE"
                ? "يعود إلى المسار من المرحلة التي وقف عندها."
                : "يخرج من مسار هذه الوظيفة."}
          </p>
          <div className="field mt-2">
            <label className="label" htmlFor="dec-note">
              ملاحظة
            </label>
            <textarea
              id="dec-note"
              className="input"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </>
      )}
    </Modal>
  );
}

/* ==========================================================================
   إدخال مرشّح من الداخل
   ========================================================================== */

/**
 * من وصل بالبريد أو حضر بنفسه.
 *
 * الهاتف مطلوبٌ ولا يُترك: هو هويّة المرشّح في قاعدة البيانات، ومن أُدخل
 * بلا هاتفٍ يُنشأ له ملفٌّ ثانٍ في المرّة القادمة فيضيع تاريخُه.
 */
/* ==========================================================================
   أعمدة المسار
   ========================================================================== */

/**
 * ضبطُ أعمدة المسار.
 *
 * ## ولماذا تُضاف أعمدةٌ أصلًا
 *
 * المراحلُ السبعُ عرفٌ شائعٌ لا قانون: شركةُ برمجيّاتٍ تضع «اختبارًا
 * تقنيًّا» بين الفرز والمقابلة، ووكالةُ إعلانٍ تضع «مراجعةَ أعمال»، ومصنعٌ
 * يضع «فحصًا طبّيًّا» قبل التعيين. وبلا ذلك تُحشر هذه كلُّها في «مقابلة»
 * فيضيع الفرقُ بين من ينتظر اختبارًا ومن ينتظر موعدًا — وهو الفرقُ الذي
 * يُعرف به من يتأخّر ولماذا.
 *
 * ## ولماذا لا يُحذف عمود
 *
 * الطلباتُ تحمل مفتاحَه نصًّا: حذفُه يترك من فيه في عمودٍ لا وجودَ له،
 * فيختفون من اللوحة وهم ما زالوا ينتظرون. والإيقافُ يُبقيهم ظاهرين في
 * عمودٍ مُعلَّمٍ «موقوف» ليُنقلوا منه.
 */
function StagesDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<StageRow | "new" | null>(null);

  const list = useQuery({
    queryKey: ["recruit", "stages"],
    queryFn: () => api<StageRow[]>("/hr/recruit/stages"),
  });

  const rows = list.data ?? [];

  const reorder = useMutation({
    mutationFn: (keys: string[]) =>
      api("/hr/recruit/stages/reorder", { method: "POST", body: { keys } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الترتيب"),
  });

  /*
   * الترتيبُ يُرسل كاملًا — الفعّالُ أوّلًا ثمّ الموقوف.
   *
   * إرسالُ الفعّال وحدَه كان يترك الموقوفةَ على أرقامها القديمة فتصطدم
   * بالجديدة. والخادمُ يدفع ما لم يُرسَل إلى الآخر على أيّ حال، وإرسالُه
   * صريحًا يجعل ما تراه الشاشةُ هو ما يُحفظ.
   */
  const move = (index: number, by: number) => {
    const live = rows.filter((x) => x.isActive).map((x) => x.key);
    const to = index + by;
    if (to < 0 || to >= live.length) return;
    const next = [...live];
    const [k] = next.splice(index, 1);
    next.splice(to, 0, k!);
    reorder.mutate([
      ...next,
      ...rows.filter((x) => !x.isActive).map((x) => x.key),
    ]);
  };

  const live = rows.filter((x) => x.isActive);
  const stopped = rows.filter((x) => !x.isActive);

  return (
    <Modal
      title="أعمدة المسار"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إغلاق
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setEditing("new")}
            disabled={rows.length >= MAX_PIPELINE_STAGES}
          >
            <Icon name="plus" size={14} />
            عمود جديد
          </button>
        </>
      }
    >
      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {/*
        الترتيبُ معنًى لا عرض: به يُعرف التقدّمُ من التراجع، وبه يُقاس قمعُ
        التوظيف. ويُقال هنا لأنّ من ينقل عمودًا لا يخطر له أنّه يغيّر ما
        يُحسب به شيءٌ آخر.
      */}
      <Notice title="الترتيب ليس للعرض وحده" icon="info">
        به يُعرف التقدّمُ من التراجع في سجلّ كلّ طلب، وبه يُقرأ قمعُ التوظيف في
        «نظرة عامة». فمن ينقل «عرض عمل» قبل «مقابلة» يجعل الانتقالَ إلى المقابلة
        تراجعًا — وهو قرارُه، ولكنّه يقع.
      </Notice>

      {rows.length > 0 && (
        <div className="table-wrap mt-4">
          <table className="table">
            <thead>
              <tr>
                <th>العمود</th>
                <th>ما يُنتظر فيه</th>
                <th>الطلبات</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {live.map((x, i) => (
                <tr key={x.id}>
                  <td>
                    <Badge kind={x.kind}>{x.label}</Badge>
                    <div className="tiny mute num" dir="ltr">
                      {x.key}
                    </div>
                    {x.required && <div className="tiny mute">لا يُوقَف</div>}
                  </td>
                  <td className="cell-mute">{x.action ?? "—"}</td>
                  <td className="num">{num(x.count)}</td>
                  <td>
                    <span className="row" style={{ gap: 4 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reorder.isPending}
                        title="قدّمه"
                      >
                        <Icon name="chevronUp" size={12} />
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => move(i, 1)}
                        disabled={i === live.length - 1 || reorder.isPending}
                        title="أخّره"
                      >
                        <Icon name="chevronDown" size={12} />
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditing(x)}
                      >
                        تعديل
                      </button>
                    </span>
                  </td>
                </tr>
              ))}

              {stopped.map((x) => (
                <tr key={x.id}>
                  <td>
                    <Badge kind="neutral">{x.label}</Badge>
                    <div className="tiny mute num" dir="ltr">
                      {x.key}
                    </div>
                    <div className="tiny mute">موقوف</div>
                  </td>
                  <td className="cell-mute">—</td>
                  <td className="num">
                    {num(x.count)}
                    {x.count > 0 && (
                      <div className="tiny" style={{ color: "var(--warning)" }}>
                        ما زالوا فيه
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm"
                      onClick={() => setEditing(x)}
                    >
                      تعديل
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="tiny mute mt-4">
        لا حذفَ لعمود: الطلباتُ تحمل مفتاحَه نصًّا، وحذفُه يترك من فيه في عمودٍ
        لا وجودَ له فيختفون من اللوحة وهم ما زالوا ينتظرون. والموقوفُ يبقى
        ظاهرًا في اللوحة معلَّمًا «موقوف» ما دام فيه أحد.
      </p>

      {editing && (
        <StageDialog
          stage={editing === "new" ? null : editing}
          taken={rows.map((x) => x.key)}
          nextOrder={live.length + 1}
          onClose={() => setEditing(null)}
        />
      )}
    </Modal>
  );
}

/** نموذجُ عمودٍ واحد. */
function StageDialog({
  stage,
  taken,
  nextOrder,
  onClose,
}: {
  stage: StageRow | null;
  taken: string[];
  nextOrder: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [label, setLabel] = useState(stage?.label ?? "");
  const [key, setKey] = useState(stage?.key ?? "");
  const [tone, setTone] = useState(stage?.kind ?? "neutral");
  const [action, setAction] = useState(stage?.action ?? "");
  const [description, setDescription] = useState(stage?.description ?? "");
  const [isActive, setIsActive] = useState(stage?.isActive ?? true);
  /* المفتاحُ يُقترح من الاسم ما لم يلمسه أحد — ومن لمسه صار صاحبَه. */
  const [keyTouched, setKeyTouched] = useState(false);

  const suggested = stage
    ? stage.key
    : keyTouched
      ? key
      : suggestStageKey(label, taken);

  const valid =
    label.trim().length >= 2 &&
    (stage !== null || STAGE_KEY_PATTERN.test(suggested));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...(stage ? {} : { key: suggested }),
        label: label.trim(),
        tone,
        action: action.trim() || null,
        description: description.trim() || null,
        order: stage?.order ?? nextOrder,
        isActive,
      };
      return stage
        ? api("/hr/recruit/stages/" + stage.id, { method: "PATCH", body })
        : api("/hr/recruit/stages", { method: "POST", body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast(stage ? "حُفظ العمود" : "أُضيف العمود");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ"),
  });

  return (
    <Modal
      title={stage ? "تعديل " + stage.label : "عمود جديد"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ" : "حفظ"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="st-label">
            اسم العمود
          </label>
          <input
            id="st-label"
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="اختبار تقني"
            maxLength={60}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="st-key">
            المفتاح
          </label>
          <input
            id="st-key"
            className="input num"
            dir="ltr"
            value={suggested}
            onChange={(e) => {
              setKeyTouched(true);
              setKey(e.target.value.toUpperCase());
            }}
            disabled={stage !== null}
            maxLength={31}
          />
          {/*
            المفتاحُ يُكتب في صفوف الطلبات نصًّا، فتغييرُه بعد الإنشاء يترك
            طلباتٍ في عمودٍ لا وجودَ له. ولذلك يُقفل بعد أوّل حفظة.
          */}
          <span className="tiny mute">
            {stage
              ? "لا يتغيّر بعد الإنشاء: الطلباتُ تحمله نصًّا، وتغييرُه يتركها في عمودٍ لا وجودَ له."
              : "حروفٌ لاتينيّةٌ كبيرةٌ وأرقامٌ وشرطةٌ سفليّة — يُقترح من الاسم ويُعدَّل، ولا يتغيّر بعد الحفظ."}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="st-tone">
            اللون
          </label>
          <select
            id="st-tone"
            className="select"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
          >
            <option value="neutral">رمادي</option>
            <option value="info">أزرق</option>
            <option value="brand">أساسي</option>
            <option value="success">أخضر</option>
            <option value="warning">برتقالي</option>
            <option value="danger">أحمر</option>
          </select>
        </div>

        <div className="field">
          <span className="label">معاينة</span>
          <Badge kind={tone}>{label.trim() || "اسم العمود"}</Badge>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="st-action">
            ما يُنتظر فعلُه هنا
          </label>
          <input
            id="st-action"
            className="input"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="أرسل الاختبار وحدّد مهلة"
            maxLength={160}
          />
          <span className="tiny mute">
            يُعرض على بطاقة كلّ طلبٍ في هذا العمود، فتصير اللوحةُ قائمةَ عملٍ لا
            سجلًّا.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="st-desc">
            ما هذا العمود
          </label>
          <input
            id="st-desc"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="أُرسل إليه اختبارٌ عمليٌّ ويُنتظر تسليمُه."
            maxLength={400}
          />
          <span className="tiny mute">يُقرأ في العمود حين يكون خاليًا.</span>
        </div>

        {stage && (
          <div className="field span-2">
            <label className="row tiny" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={isActive}
                disabled={stage.required}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              عمودٌ فعّال
            </label>
            <span className="tiny mute">
              {stage.required
                ? "هذا العمود لا يُوقَف — عنده يُصدَر العرضُ ويُفتح الملفّ الوظيفيّ."
                : "الإيقافُ لا الحذف. والموقوفُ يبقى ظاهرًا في اللوحة ما دام فيه أحد، ليُنقل من فيه."}
            </span>
            {stage.count > 0 && !isActive && (
              <span className="tiny" style={{ color: "var(--warning)" }}>
                فيه {num(stage.count)} طلبًا — سيبقى ظاهرًا معلَّمًا «موقوف» حتى
                يُنقل من فيه.
              </span>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function AddCandidateDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const jobs = useQuery({
    queryKey: ["recruit", "jobs", "lite"],
    queryFn: () => api<JobLite[]>("/hr/recruit/jobs"),
  });

  const [jobPostId, setJobPostId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [headline, setHeadline] = useState("");
  const [source, setSource] = useState("OTHER");
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const job = jobs.data?.find((j) => j.id === jobPostId);

  const detail = useQuery({
    queryKey: ["recruit", "job", jobPostId],
    queryFn: () =>
      api<{
        formFields: {
          key: string;
          label: string;
          type: string;
          options: string[];
          required: boolean;
          hint: string | null;
        }[];
      }>(`/hr/recruit/jobs/${jobPostId}`),
    enabled: Boolean(jobPostId),
  });

  const fields = detail.data?.formFields ?? [];
  const valid =
    jobPostId !== "" && name.trim().length >= 2 && phone.trim() !== "";

  const save = useMutation({
    mutationFn: () =>
      api("/hr/recruit/applications", {
        method: "POST",
        body: {
          jobPostId,
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || null,
          headline: headline.trim() || null,
          source,
          notes: notes.trim() || null,
          answers: toAnswers(fields, answers),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast("أُضيف المرشّح");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّرت الإضافة"),
  });

  return (
    <Modal
      title="أضف مرشّحًا"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ" : "أضف"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ap-job">
            الوظيفة
          </label>
          <select
            id="ap-job"
            className="select"
            value={jobPostId}
            onChange={(e) => setJobPostId(e.target.value)}
          >
            <option value="">اختر وظيفة</option>
            {(jobs.data ?? []).map((j) => (
              <option key={j.id} value={j.id}>
                {j.title}
              </option>
            ))}
          </select>
          {job && job.status !== "OPEN" && (
            <span className="tiny mute">
              هذه الوظيفة ليست مفتوحة — الإدخال من الداخل يعمل مع ذلك.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label req" htmlFor="ap-name">
            الاسم
          </label>
          <input
            id="ap-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ap-phone">
            الهاتف
          </label>
          <input
            id="ap-phone"
            className="input num"
            dir="ltr"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ap-email">
            البريد
          </label>
          <input
            id="ap-email"
            className="input"
            dir="ltr"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ap-headline">
            المسمّى الحالي
          </label>
          <input
            id="ap-headline"
            className="input"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ap-source">
            المصدر
          </label>
          <select
            id="ap-source"
            className="select"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            {CANDIDATE_SOURCES.map((s) => (
              <option key={s} value={s}>
                {CANDIDATE_SOURCE_MAP[s].label}
              </option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ap-notes">
            ملاحظات داخلية
          </label>
          <textarea
            id="ap-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {fields.length > 0 && (
          <CustomFieldInputs
            defs={fields.map((f) => ({
              key: f.key,
              label: f.label,
              type: f.type as never,
              options: f.options,
              required: f.required,
              hint: f.hint,
            }))}
            values={answers}
            onChange={(k, v) => setAnswers((p) => ({ ...p, [k]: v }))}
            idPrefix="ap"
          />
        )}
      </div>
    </Modal>
  );
}

/**
 * تحويل قيم الخانات إلى ما يقبله الخادم.
 *
 * كلُّ شيء نصٌّ في الخانة — حتى الرقم — فيُحوَّل هنا. والفارغُ يُحذف لا
 * يُرسل فارغًا: حقلٌ اختياريٌّ تُرك خاليًا ليس إجابةً بنصٍّ فارغ.
 */
function toAnswers(
  fields: { key: string; type: string }[],
  values: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = (values[f.key] ?? "").trim();
    if (raw === "") continue;
    if (f.type === "NUMBER") out[f.key] = Number(raw);
    else if (f.type === "BOOLEAN") out[f.key] = raw === "true";
    else out[f.key] = raw;
  }
  return out;
}
